import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Label,
  Table,
  Spinner,
  Alert,
  UncontrolledTooltip,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import SysAdminService from "../../services/sysadmin";

function BillingReport() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [costs, setCosts] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    loadCompanies();
    // Set default dates: last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    setToDate(today);
    setFromDate(thirtyDaysAgo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateForApi = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const formatDateDisplay = (dateStr) => {
    // Convert "2026-03-16" to "16/03/2026"
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const loadCompanies = async () => {
    try {
      const res = await SysAdminService.getCompaniesStats();
      if (res.data?.data) {
        setCompanies(res.data.data);
      }
    } catch (error) {
      toast.error(t("SYSADMIN.LOAD_ERROR") || "שגיאה בטעינת חברות");
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedCompanyId) {
      toast.error(t("SYSADMIN.SELECT_COMPANY_REQUIRED") || "נא לבחור חברה");
      return;
    }
    if (!fromDate || !toDate) {
      toast.error(t("SYSADMIN.SELECT_DATES_REQUIRED") || "נא לבחור תאריכים");
      return;
    }

    setIsLoading(true);
    setErrorMsg("");
    setHasSearched(true);
    try {
      const res = await SysAdminService.getBillingReport({
        companyId: parseInt(selectedCompanyId),
        from: formatDateForApi(fromDate),
        to: formatDateForApi(toDate),
      });
      if (res.data?.status === 200 && res.data?.data) {
        setReportData(res.data.data);
        setCosts(res.data.costs || null);
      } else if (res.data?.message) {
        setReportData([]);
        setErrorMsg(res.data.message);
      } else {
        setReportData([]);
      }
    } catch (error) {
      console.error("Billing report error:", error);
      setErrorMsg(error?.response?.data?.message || error?.message || "שגיאה בהפקת דוח");
      toast.error(t("SYSADMIN.REPORT_ERROR") || "שגיאה בהפקת דוח");
      setReportData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const totals = reportData.reduce(
    (acc, row) => ({
      smsSegments: acc.smsSegments + (row.smsSegments || 0),
      whatsappConversations: acc.whatsappConversations + (row.whatsappConversations || 0),
      smsCostTotal: acc.smsCostTotal + (row.smsCostTotal || 0),
      whatsappCostTotal: acc.whatsappCostTotal + (row.whatsappCostTotal || 0),
      totalCost: acc.totalCost + (row.totalCost || 0),
    }),
    { smsSegments: 0, whatsappConversations: 0, smsCostTotal: 0, whatsappCostTotal: 0, totalCost: 0 }
  );

  return (
    <div className="page-content-inner">
      <h4 className="mb-4">
        <i className="ri-file-list-3-line me-2"></i>
        {t("SYSADMIN.BILLING_REPORT") || "דוח חיוב הודעות"}
      </h4>

      {/* Filters */}
      <Card>
        <CardBody>
          <Row className="align-items-end">
            <Col md={3}>
              <Label>{t("SYSADMIN.COMPANY") || "חברה"}</Label>
              <Input
                type="select"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                disabled={isLoadingCompanies}
              >
                <option value="">{t("SYSADMIN.SELECT_COMPANY") || "-- בחר חברה --"}</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>{t("SYSADMIN.FROM_DATE") || "מתאריך"}</Label>
              <DatePicker
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="dd/MM/yyyy"
                maxDate={toDate || new Date()}
              />
            </Col>
            <Col md={3}>
              <Label>{t("SYSADMIN.TO_DATE") || "עד תאריך"}</Label>
              <DatePicker
                selected={toDate}
                onChange={(date) => setToDate(date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="dd/MM/yyyy"
                minDate={fromDate}
                maxDate={new Date()}
              />
            </Col>
            <Col md={3}>
              <Button
                color="primary"
                onClick={handleGenerateReport}
                disabled={isLoading}
                className="w-100"
              >
                {isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i className="ri-search-line me-1"></i>
                    {t("SYSADMIN.GENERATE_REPORT") || "הפק דוח"}
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Cost info */}
      {costs && reportData.length > 0 && (
        <Card>
          <CardBody className="py-2">
            <small className="text-muted">
              <i className="ri-information-line me-1"></i>
              {"עלויות: "}
              SMS {costs.smsCost}₪/מקטע
              {" | "}
              WhatsApp Utility {costs.waUtilityCost}₪/שיחה
              {costs.waMarketingCost > 0 && ` | Marketing ${costs.waMarketingCost}₪/שיחה`}
              {costs.waAuthCost > 0 && ` | Auth ${costs.waAuthCost}₪/שיחה`}
            </small>
          </CardBody>
        </Card>
      )}

      {/* Error message */}
      {errorMsg && (
        <Alert color="danger">
          <i className="ri-error-warning-line me-2"></i>
          {errorMsg}
        </Alert>
      )}

      {/* Results */}
      {reportData.length > 0 && (
        <Card>
          <CardBody>
            <h5 className="mb-3">{t("SYSADMIN.REPORT_RESULTS") || "תוצאות הדוח"}</h5>
            <div className="table-responsive">
              <Table className="table-bordered table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t("SYSADMIN.DATE") || "תאריך"}</th>
                    <th className="text-center" id="smsHeader">
                      {"מקטעי SMS"}
                    </th>
                    <UncontrolledTooltip target="smsHeader" placement="top">
                      {"חיוב לפי מקטע — כל מקטע עד 134 תווים"}
                    </UncontrolledTooltip>
                    <th className="text-center" id="waHeader">
                      {"שיחות WhatsApp"}
                    </th>
                    <UncontrolledTooltip target="waHeader" placement="top">
                      {"חיוב לפי שיחה — חלון 24 שעות לאותו מספר, המחיר הגבוה מבין הקטגוריות"}
                    </UncontrolledTooltip>
                    <th className="text-center">{t("SYSADMIN.SMS_COST") || "עלות SMS"}</th>
                    <th className="text-center">{t("SYSADMIN.WHATSAPP_COST_LABEL") || "עלות WhatsApp"}</th>
                    <th className="text-center">{t("SYSADMIN.TOTAL_COST") || "סה\"כ עלות"}</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, idx) => (
                    <tr key={idx}>
                      <td>{formatDateDisplay(row.date)}</td>
                      <td className="text-center">{row.smsSegments || 0}</td>
                      <td className="text-center">
                        {row.whatsappConversations || 0}
                        {row.whatsappBreakdown && (row.whatsappBreakdown.marketing > 0 || row.whatsappBreakdown.authentication > 0) && (
                          <small className="text-muted d-block" style={{ fontSize: "0.75em" }}>
                            {row.whatsappBreakdown.utility > 0 && `U:${row.whatsappBreakdown.utility}`}
                            {row.whatsappBreakdown.marketing > 0 && ` M:${row.whatsappBreakdown.marketing}`}
                            {row.whatsappBreakdown.authentication > 0 && ` A:${row.whatsappBreakdown.authentication}`}
                          </small>
                        )}
                      </td>
                      <td className="text-center">{(row.smsCostTotal || 0).toFixed(2)}₪</td>
                      <td className="text-center">{(row.whatsappCostTotal || 0).toFixed(2)}₪</td>
                      <td className="text-center fw-bold">{(row.totalCost || 0).toFixed(2)}₪</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr className="fw-bold">
                    <td>{t("SYSADMIN.TOTAL") || "סה\"כ"}</td>
                    <td className="text-center">{totals.smsSegments}</td>
                    <td className="text-center">{totals.whatsappConversations}</td>
                    <td className="text-center">{totals.smsCostTotal.toFixed(2)}₪</td>
                    <td className="text-center">{totals.whatsappCostTotal.toFixed(2)}₪</td>
                    <td className="text-center">{totals.totalCost.toFixed(2)}₪</td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {reportData.length === 0 && !isLoading && hasSearched && !errorMsg && (
        <Card>
          <CardBody className="text-center text-muted py-5">
            <i className="ri-file-search-line" style={{ fontSize: "48px" }}></i>
            <p className="mt-3">{t("SYSADMIN.NO_DATA") || "לא נמצאו נתונים לתקופה שנבחרה."}</p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

export default BillingReport;
